import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { useDomain } from "@/hooks/useDomain";
import { useLanguage } from "@/contexts/LanguageContext";
import { SERVICE_DETAILS } from "@/lib/serviceDetails";
import { pickLocalized } from "@/contexts/SiteContentContext";

const COPY = {
  de: {
    eyebrow: "Spezialisiert auf",
    label: "Mehr erfahren",
  },
  en: {
    eyebrow: "Specialised in",
    label: "Learn more",
  },
  es: {
    eyebrow: "Especializado en",
    label: "Más información",
  },
};

/**
 * Slim banner shown only on focused (non-main) domains.
 * Deep-links to the relevant service detail page so the user immediately lands
 * on the keyword-optimised SEO sub-page.
 */
const FocusedServiceBanner = () => {
  const domain = useDomain();
  const { locale } = useLanguage();
  const copy = COPY[locale] || COPY.de;

  if (domain.isMain || !domain.focusedServiceSlug) return null;
  const detail = SERVICE_DETAILS[domain.focusedServiceSlug];
  if (!detail) return null;

  const serviceTitle = pickLocalized(detail.title, locale);

  return (
    <section
      id="service-focus"
      data-testid="focused-service-banner"
      className="container-px mx-auto max-w-7xl pt-6 scroll-mt-24"
    >
      <Link
        to={`/services/${domain.focusedServiceSlug}`}
        className="group block rounded-3xl bg-gradient-to-r from-sky-50 to-white border border-sky-100 px-6 py-5 hover:shadow-[0_10px_30px_rgba(2,132,199,0.10)] transition-shadow"
      >
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-2xl bg-sky-600 text-white grid place-items-center shrink-0">
              <Sparkles className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-sky-700 font-medium">
                {copy.eyebrow} · {domain.brandName}
              </div>
              <div className="font-display text-base lg:text-xl font-medium text-neutral-950 truncate">
                {serviceTitle}
              </div>
            </div>
          </div>
          <div className="inline-flex items-center gap-2 text-sm text-sky-700 font-medium shrink-0">
            {copy.label}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </div>
        </div>
      </Link>
    </section>
  );
};

export default FocusedServiceBanner;
